/**
 * `useWhoami()` · GET `/api/v1/diagnostics/whoami`
 *
 * Returns the resolved-server-side identity of the current user,
 * including the `is_admin` flag the React app needs to gate
 * admin-only surfaces (e.g. the "Edit templates (Admin)" button on
 * `/app/avatars`).
 *
 * Cached via React Query for 5 minutes since the role rarely changes
 * mid-session. Anonymous callers get 401, which React Query treats as
 * a query error — consumers should fall back to "not admin" when
 * `data` is null.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';

export interface WhoamiResponse {
  user_id: number;
  email: string;
  display_name: string | null;
  role: string;
  is_admin: boolean;
  /** Whether the user finished (or skipped) the onboarding funnel. */
  is_onboarded: boolean;
}

export const WHOAMI_KEY = ['auth', 'whoami'] as const;

export function useWhoami(): UseQueryResult<WhoamiResponse, Error> {
  return useQuery({
    queryKey: WHOAMI_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/diagnostics/whoami');
      if (!raw || typeof raw !== 'object') {
        throw new Error('whoami response is not an object');
      }
      const r = raw as Record<string, unknown>;
      return {
        user_id: typeof r.user_id === 'number' ? r.user_id : 0,
        email: typeof r.email === 'string' ? r.email : '',
        display_name: typeof r.display_name === 'string' ? r.display_name : null,
        role: typeof r.role === 'string' ? r.role : 'user',
        is_admin: r.is_admin === true,
        is_onboarded: r.is_onboarded === true,
      };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Convenience selector: returns true when the signed-in user is an admin. */
export function useIsAdmin(): boolean {
  const q = useWhoami();
  return q.data?.is_admin ?? false;
}
